/**
 * Toast Notification Utilities
 *
 * Centralized toast notification functions using Sonner library.
 * Provides standardized toast behavior for notification system.
 *
 * Features:
 * - High-urgency notifications trigger automatic toasts
 * - Toast duration: 8 seconds for high urgency
 * - Max 5 toasts stacked vertically (handled by Sonner)
 * - Action buttons for navigation to notification target
 * - Manual dismiss functionality (marks notification as read)
 * - Action button click marks notification as read
 * - Auto-close timeout does NOT mark as read (user may not have seen it)
 * - Structured logging for toast delivery
 *
 * @module utils/toast
 */

import { toast } from 'sonner';
import type { Notification } from '@/types/notification';
import logger from '@/utils/logger';

/**
 * Toast duration in milliseconds for high-urgency notifications
 */
const HIGH_URGENCY_DURATION = 8000; // 8 seconds

/**
 * Display a toast notification for a high-urgency notification
 *
 * @param notification - The notification to display as a toast
 * @param onActionClick - Optional callback when action button is clicked
 * @param onMarkRead - Optional callback when notification should be marked as read (on manual dismiss or action click)
 */
export function showNotificationToast(
  notification: Notification,
  onActionClick?: (actionUrl: string) => void,
  onMarkRead?: (globalId: string) => void | Promise<void>
): void {
  logger.info('Toast', 'Displaying notification toast', {
    globalId: notification.globalId,
    type: notification.notificationType,
    urgency: notification.urgency,
  });

  // Only show toasts for high-urgency notifications
  if (notification.urgency !== 'high') {
    logger.debug('Toast', 'Skipping toast for non-high urgency notification', {
      urgency: notification.urgency,
    });
    return;
  }

  const toastId = toast(notification.title, {
    description: notification.body || undefined,
    duration: HIGH_URGENCY_DURATION,
    action: notification.actionUrl
      ? {
          label: 'View',
          onClick: () => {
            logger.info('Toast', 'Action button clicked', {
              globalId: notification.globalId,
              actionUrl: notification.actionUrl,
            });
            // Mark as read when action button is clicked (user engaged with notification)
            if (onMarkRead) {
              onMarkRead(notification.globalId);
            }
            if (onActionClick && notification.actionUrl) {
              onActionClick(notification.actionUrl);
            }
          },
        }
      : undefined,
    closeButton: true, // Enable manual dismiss
    onDismiss: () => {
      // Called when user manually dismisses the toast (clicks X button or swipes away)
      logger.info('Toast', 'Toast manually dismissed, marking notification as read', {
        globalId: notification.globalId,
      });
      if (onMarkRead) {
        onMarkRead(notification.globalId);
      }
    },
  });

  logger.debug('Toast', 'Toast displayed successfully', {
    toastId,
    globalId: notification.globalId,
  });
}

/**
 * Display a success toast notification
 *
 * @param title - Toast title
 * @param description - Optional toast description
 */
export function showSuccessToast(title: string, description?: string): void {
  logger.info('Toast', 'Displaying success toast', { title });
  toast.success(title, {
    description,
    duration: 4000, // Shorter duration for success messages
  });
}

/**
 * Display an error toast notification
 *
 * @param title - Toast title
 * @param description - Optional toast description
 */
export function showErrorToast(title: string, description?: string): void {
  logger.error('Toast', 'Displaying error toast', { title });
  toast.error(title, {
    description,
    duration: 6000, // Longer duration for errors
  });
}

/**
 * Display an info toast notification
 *
 * @param title - Toast title
 * @param description - Optional toast description
 */
export function showInfoToast(title: string, description?: string): void {
  logger.info('Toast', 'Displaying info toast', { title });
  toast.info(title, {
    description,
    duration: 4000,
  });
}

/**
 * Display a warning toast notification
 *
 * @param title - Toast title
 * @param description - Optional toast description
 */
export function showWarningToast(title: string, description?: string): void {
  logger.warn('Toast', 'Displaying warning toast', { title });
  toast.warning(title, {
    description,
    duration: 5000,
  });
}
